import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateQueueConcurrency, type Queue } from "@/lib/api/system";
import {
  UpdateQueueForm,
  formToUpdateApi,
  type QueueFormValues,
} from "./queue-form";

interface UpdateQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  queue: Queue | null;
}

export function UpdateQueueModal({
  isOpen,
  onClose,
  onSuccess,
  queue,
}: UpdateQueueModalProps) {
  const { toast } = useToast();
  const updateQueueConcurrency = useUpdateQueueConcurrency();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!queue) return null;

  const handleSubmit = async (values: QueueFormValues) => {
    setIsSubmitting(true);

    try {
      const payload = formToUpdateApi(values, queue.id);
      const result = await updateQueueConcurrency(payload);

      if (result.success) {
        toast({
          title: "Updated",
          description: "Queue concurrency has been updated successfully",
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to update queue concurrency",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update queue concurrency",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Queue</DialogTitle>
          <DialogDescription>
            Update the max concurrency for this queue
          </DialogDescription>
        </DialogHeader>

        <UpdateQueueForm
          queue={queue}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
